import { Callback } from "game-common/models"
import { Container, Graphics } from "pixi.js"

import { CLOSE_BUTTON_RADIUS, CloseButton } from "./close_button"
import { disposeTextTextures } from "../text/dispose_text"

export interface DialogOptions {
    padding?: number
    x?: number
    y?: number
    width?: number
    height?: number
    content?: Graphics
    borderColor?: number
    backgroundColor?: number
    backgroundAlpha?: number
    closeCallback?: Function
    onCloseClick?: Callback
    onMouseOver?: Function
    onMouseOut?: Function
    center?: boolean
    plain?: boolean
    unescapeable?: boolean
    // Where to hang the circular close button, in CONTENT coordinates (i.e. the same space the
    // content graphic draws in, before the dialog's padding offset). Pass the top-right corner of the
    // panel the content actually paints when that panel is inset from the dialog rect -- a full-screen
    // modal that only draws a right-docked panel, say -- so the button straddles the panel's corner
    // instead of the rect's. Defaults to just inside the dialog's own top-right corner.
    closeAnchor?: { x: number; y: number }
}

export class Dialog {
    parent: Container
    padding: number
    x: number
    y: number
    width: number
    height?: number
    content: Graphics
    backgroundColor: number
    borderColor: number
    backgroundAlpha: number
    private dialog: Graphics
    private closeCallback?: Function
    private onCloseClick?: Callback
    private onMouseOver?: Function
    private onMouseOut?: Function
    private closed: boolean
    private plain: boolean
    private unescapeable: boolean
    private closeAnchor?: { x: number; y: number }

    constructor(
        parent: Container,
        {
            padding = 0,
            x = 0,
            y = 0,
            content = new Graphics(),
            borderColor = 0xffffff,
            backgroundColor = 0x000000,
            backgroundAlpha = 0.91,
            width,
            height,
            closeCallback,
            onCloseClick,
            onMouseOver,
            onMouseOut,
            plain,
            unescapeable,
            closeAnchor,
        }: DialogOptions,
    ) {
        this.parent = parent
        this.padding = padding
        this.content = content
        this.x = x
        this.y = y
        this.backgroundAlpha = backgroundAlpha
        this.backgroundColor = backgroundColor
        this.width = width
        this.height = height
        this.closeCallback = closeCallback
        this.onCloseClick = onCloseClick
        this.borderColor = borderColor
        this.onMouseOver = onMouseOver
        this.onMouseOut = onMouseOut
        this.plain = plain
        this.unescapeable = unescapeable
        this.closeAnchor = closeAnchor
    }

    update = () => {
        const dialog = new Graphics()
        this.dialog = dialog
        this.dialog.zIndex = 3
        this.parent.addChild(dialog)

        this.content.x = this.padding
        this.content.y = this.padding

        dialog.addChild(this.content)

        if (this.width) {
            const spacer = new Graphics()
            spacer.x = 0
            spacer.y = 0
            spacer.drawRect(0, 0, this.width, 1)
            spacer.width = this.width
            dialog.addChild(spacer)
        }
        if (this.height) {
            const spacer = new Graphics()
            spacer.x = 0
            spacer.y = 0
            spacer.drawRect(0, 0, 1, this.height)
            spacer.height = this.height
            dialog.addChild(spacer)
        }

        dialog.x = this.x
        dialog.y = this.y
        if (!this.plain) {
            dialog.lineStyle(1, this.borderColor, 0.5)
        }
        dialog.beginFill(this.backgroundColor, this.backgroundAlpha)
        if (!this.plain) {
            dialog.drawRoundedRect(0, 0, dialog.width + this.padding * 2, dialog.height + this.padding * 2, 8)
        } else {
            dialog.drawRect(0, 0, dialog.width + this.padding * 2, dialog.height + this.padding * 2)
        }
        dialog.endFill()

        if (!this.unescapeable) {
            // Hung on the top-right corner: the button draws from its own center, so pinning it at a
            // corner leaves it half over the edge. dialog.width is read after the backdrop is drawn,
            // so it is the width of the visible rect (content + padding on both sides).
            const close = new CloseButton({ onClick: this.onCloseClick })
            if (this.closeAnchor) {
                close.x = this.closeAnchor.x + this.padding
                close.y = this.closeAnchor.y + this.padding
            } else {
                // Nothing inset to hang off (the backdrop runs to the screen edge), so tuck the
                // button just inside the corner where it can't be clipped off-screen.
                close.x = dialog.width - CLOSE_BUTTON_RADIUS - 6
                close.y = CLOSE_BUTTON_RADIUS + 6
            }

            dialog.addChild(close)
        }

        dialog.interactive = !!this.onMouseOut || !!this.onMouseOver
        if (this.onMouseOver) {
            dialog.on("mouseover", () => this.onMouseOver())
        }
        if (this.onMouseOut) {
            dialog.on("mouseout", () => this.onMouseOut())
        }
    }

    close = () => {
        if (this.closed) {
            return
        }
        // Free the dialog content's per-instance Text textures on close -- every modal/dialog
        // (inventory, character sheet, etc.) builds Text via TextRenderer, and detaching without
        // this strands each one's canvas-backed texture (not reclaimed by GC). Shared item-sprite
        // textures are left intact by disposeTextTextures.
        disposeTextTextures(this.dialog)
        this.parent.removeChild(this.dialog)
        this.closeCallback()
        this.closed = true
    }
}
